<script setup lang="ts">
import DatePicker from './DatePicker.vue';
import { useField } from 'vee-validate';
import { computed, toRef } from 'vue';
import { DateTime } from 'luxon';
import { toUserTime } from '@/components/formatters/utils';
import { useVeeFormProps } from '../utils';
import { VeeDatePickerProps } from '../types';

const props = withDefaults(defineProps<VeeDatePickerProps>(), {
  format: 'DateTime',
});

const veeProps = useVeeFormProps(props);

const emits = defineEmits<{
  change: [dateTime: DateTime | undefined];
}>();

const { errorMessage, value, handleChange, handleBlur, meta } = useField<string | undefined>(
  toRef(() => props.name),
  undefined,
  {
    validateOnValueUpdate: false,
  },
);

const modelValue = computed(() => {
  if (!value.value) {
    return undefined;
  }

  const dateTime =
    props.format === 'Date' ? DateTime.fromISO(value.value) : toUserTime(value.value);

  return dateTime?.isValid ? dateTime : undefined;
});

function onUpdate(input: DateTime | undefined) {
  const nextValue = props.format === 'Date' ? input?.toISODate() : input?.setZone('UTC').toISO();

  handleChange(nextValue, meta.touched);

  emits('change', input);
}

function onBlur(event: FocusEvent) {
  handleBlur(event, true);
}
</script>

<template>
  <DatePicker
    v-bind="veeProps"
    :modelValue="modelValue"
    :errorMessage="errorMessage"
    @update:modelValue="onUpdate"
    @blur="onBlur"
  />
</template>
